import { CloseIcon } from "@/components/features/SquadBuilder/RightPanel/PanelHeader/PanelHeader";
import {
  GAME_NAME,
  IS_ARCHIVE_EDITION,
  OTHER_EDITION_NAME,
  OTHER_EDITION_URL,
  SHOW_FC27_PROMO,
} from "@/config/game";
import { cn } from "@/lib/utils";
import { Trans, useTranslation } from "@repo/shared/i18n";

export const CURRENT_FEATURE_INDEX = 20;

/*
  Whether this build has anything to put in the bar at all.

  The archive deploy always offers the way to the current edition — anyone
  seeing it arrived deliberately. Only the current deploy's outbound promo
  waits for the flag.

  NavigationBar reserves the band above the nav, so it has to read the same
  answer: a banner that renders null while the layout still holds space for it
  leaves a gap.
*/
export const FEATURES_BANNER_ENABLED = IS_ARCHIVE_EDITION || SHOW_FC27_PROMO;

/*
  The whole bar is a plain <a>, not a react-router <Link>. It crosses to a
  different bucket behind a different CloudFront behavior, so it needs a full
  page load — a <Link> would be handled client-side, fail to match this
  deployment's routes, and land on the 404 catch-all. That is also why "here"
  is a styled span rather than a nested anchor, which would be invalid.
*/
export const FeaturesBanner = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation("nav");
  if (!FEATURES_BANNER_ENABLED) return null;

  return (
    <a
      href={OTHER_EDITION_URL}
      className={cn(
        "flex flex-row justify-center items-center pl-2 text-sm desktop:text-sm bg-primary text-on-primary sticky top-0 w-full z-40 cursor-pointer h-9 transition-colors hover:bg-primary-hover",
      )}
    >
      <div className="flex flex-row justify-between w-full text-center">
        <div />
        <div className="my-auto flex items-center gap-2 overflow-hidden">
          {/* Mobile text (< 769px) — the whole bar is the target, so one line */}
          <span className="mobile:hidden truncate">
            {t("editionBanner.goTo", { edition: OTHER_EDITION_NAME })}
          </span>
          {/* Desktop text (>= 769px) */}
          <span className="hidden mobile:inline truncate">
            {IS_ARCHIVE_EDITION ? (
              <Trans
                ns="nav"
                i18nKey="editionBanner.staged"
                values={{ game: GAME_NAME, edition: OTHER_EDITION_NAME }}
                components={{ hl: <span className="font-bold underline" /> }}
              />
            ) : (
              <Trans
                ns="nav"
                i18nKey="editionBanner.live"
                values={{ edition: OTHER_EDITION_NAME }}
                components={{
                  b: <span className="font-bold" />,
                  hl: <span className="font-bold underline" />,
                }}
              />
            )}
          </span>
        </div>
        <button
          className="my-auto cursor-pointer fill-gray-800 px-4 py-2"
          onClick={(e) => {
            // Inside an anchor, so the navigation has to be suppressed too.
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
        >
          <CloseIcon color="white" />
        </button>
      </div>
    </a>
  );
};
